import { useState } from 'react'
import { Pencil, Plus, PowerOff } from 'lucide-react'
import {
  ConfirmDialog, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useDivisions, useOrgAdminMutations } from '../api'
import type { DivisionView } from '../types'
import { Select } from '@/components/ui/select'

type Draft = { id?: string; code: string; name: string; parentId: string; sortOrder: string; active: boolean }

const EMPTY: Draft = { code: '', name: '', parentId: '', sortOrder: '0', active: true }

/**
 * Divisions.
 *
 * <p>The table has existed since V11 and had no write path at all: it was populated by a
 * migration and could not be changed without another one.
 *
 * <p>There is no delete, only deactivate, and deactivating is refused while anybody is still
 * placed in the division. A 2024 assignment points at the division somebody was in during
 * 2024; removing the row would leave every historical assignment, attendance report and
 * payroll line pointing at nothing.
 */
export function DivisionsPage() {
  useBreadcrumb()
  const divisions = useDivisions()
  const { createDivision, updateDivision, deactivateDivision } = useOrgAdminMutations()
  const [draft, setDraft] = useState<Draft | null>(null)
  const [toDeactivate, setToDeactivate] = useState<DivisionView | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function save() {
    if (!draft) return
    setError(null)
    const body = {
      code: draft.code,
      name: draft.name,
      parentId: draft.parentId || null,
      sortOrder: Number(draft.sortOrder || 0),
      active: draft.active,
    }
    try {
      if (draft.id) {
        await updateDivision.mutateAsync({ id: draft.id, ...body })
      } else {
        await createDivision.mutateAsync(body)
      }
      setDraft(null)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That division could not be saved')
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Divisions"
        subtitle="The layer between a legal entity and its departments."
        actions={
          <Button type="button" onClick={() => { setError(null); setDraft({ ...EMPTY }) }}>
            <Plus className="size-4" aria-hidden="true" />
            Add a division
          </Button>
        }
      />

      <Card>
        {divisions.isPending && <Skeleton className="m-4 h-32" />}
        {divisions.isError && (
          <ErrorState error={divisions.error} onRetry={() => void divisions.refetch()} />
        )}
        {divisions.isSuccess && divisions.data.length === 0 && (
          <EmptyState title="No divisions yet" description="Add one to group departments under an entity." />
        )}
        {(divisions.data?.length ?? 0) > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full min-w-[44rem] text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                  <th scope="col" className="p-3 font-medium">Name</th>
                  <th scope="col" className="p-3 font-medium">Code</th>
                  <th scope="col" className="p-3 font-medium">Within</th>
                  <th scope="col" className="p-3 font-medium">Head</th>
                  <th scope="col" className="p-3 text-right font-medium">People</th>
                  <th scope="col" className="p-3 font-medium">Status</th>
                  <th scope="col" className="p-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {divisions.data?.map((division) => (
                  <tr key={division.id} className="border-b border-[var(--color-border)] last:border-0">
                    <td className="p-3 font-medium text-[var(--color-heading)]">{division.name}</td>
                    <td className="p-3"><code className="text-xs">{division.code}</code></td>
                    <td className="p-3 text-[var(--color-muted)]">{division.parentName ?? '—'}</td>
                    <td className="p-3 text-[var(--color-muted)]">{division.headEmployeeName ?? '—'}</td>
                    <td className="p-3 text-right tabular-nums">{division.assignedCount}</td>
                    <td className="p-3"><StatusBadge status={division.active ? 'ACTIVE' : 'INACTIVE'} /></td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button" size="icon" variant="ghost"
                          aria-label={`Edit ${division.name}`}
                          onClick={() => {
                            setError(null)
                            setDraft({
                              id: division.id, code: division.code, name: division.name,
                              parentId: division.parentId ?? '',
                              sortOrder: String(division.sortOrder ?? 0), active: division.active,
                            })
                          }}
                        >
                          <Pencil className="size-4" aria-hidden="true" />
                        </Button>
                        {division.active && (
                          <Button
                            type="button" size="icon" variant="ghost"
                            aria-label={`Deactivate ${division.name}`}
                            onClick={() => setToDeactivate(division)}
                          >
                            <PowerOff className="size-4" aria-hidden="true" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={draft !== null}
        onOpenChange={(open) => !open && setDraft(null)}
        title={draft?.id ? `Edit ${draft.name}` : 'Add a division'}
        loading={createDivision.isPending || updateDivision.isPending}
        error={error}
        onSubmit={() => void save()}
      >
        {draft && (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="div-name" required>Name</Label>
                <Input id="div-name" required value={draft.name}
                       onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="div-code" required>Code</Label>
                <Input id="div-code" required value={draft.code}
                       onChange={(e) => setDraft({ ...draft, code: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="div-parent">Within</Label>
              <Select
                id="div-parent" value={draft.parentId}
                onChange={(e) => setDraft({ ...draft, parentId: e.target.value })}
                className="w-full"
              >
                <option value="">Top level</option>
                {divisions.data
                  ?.filter((option) => option.id !== draft.id)
                  .map((option) => (
                    <option key={option.id} value={option.id}>{option.name}</option>
                  ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="div-order">Sort order</Label>
              <Input id="div-order" type="number" value={draft.sortOrder} className="w-28"
                     onChange={(e) => setDraft({ ...draft, sortOrder: e.target.value })} />
            </div>
          </>
        )}
      </FormDialog>

      <ConfirmDialog
        open={toDeactivate !== null}
        onOpenChange={(open) => !open && setToDeactivate(null)}
        title="Deactivate this division?"
        description={
          toDeactivate
            ? toDeactivate.assignedCount > 0
              ? `${toDeactivate.assignedCount} person(s) are still assigned to ${toDeactivate.name}. This will be refused until they are moved.`
              : `${toDeactivate.name} stops being offered for new assignments. Existing history keeps referring to it.`
            : undefined
        }
        confirmLabel="Deactivate"
        destructive
        loading={deactivateDivision.isPending}
        onConfirm={() => {
          if (toDeactivate) {
            deactivateDivision.mutate(toDeactivate.id, { onSettled: () => setToDeactivate(null) })
          }
        }}
      />
    </div>
  )
}
